import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import AdminNav, { AdminHeader } from "@/components/AdminNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const FIELDS = [
  { key: "processing_fee_percent", label: "Stripe % Fee", hint: "Percent processing fee (e.g. 2.9)" },
  { key: "processing_fee_fixed", label: "Stripe Fixed Fee ($)", hint: "Flat processing fee (e.g. 0.30)" },
  { key: "operational_cost_per_order", label: "Op. Cost / Order ($)", hint: "Handling + fulfilment overhead" },
  { key: "minimum_dollar_profit", label: "Min $ Profit", hint: "Reject supplier if profit below" },
  { key: "minimum_profit_margin_percent", label: "Min Margin %", hint: "Reject supplier if margin below" },
  { key: "max_supplier_cost", label: "Max Supplier Cost ($)", hint: "Reject supplier if cost exceeds" },
  { key: "max_shipping_cost", label: "Max Shipping ($)", hint: "Reject supplier if ship exceeds" },
  { key: "max_auto_order_value", label: "Max Auto Order ($)", hint: "Orders above this require human review" },
  { key: "min_supplier_inventory", label: "Min Inventory", hint: "Minimum available_qty" },
  { key: "profit_weight", label: "Profit Weight (0-1)" },
  { key: "speed_weight", label: "Speed Weight (0-1)" },
  { key: "priority_weight", label: "Priority Weight (0-1)" },
  { key: "reliability_weight", label: "Reliability Weight (0-1)" },
];

export default function AdminPricing() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/pricing-config").then(({ data }) => setCfg(data)); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/pricing-config", cfg);
      toast.success("Pricing rules saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!cfg) return (
    <div className="container-tight py-8"><AdminNav />
      <div className="font-mono text-zinc-500">LOADING...</div>
    </div>
  );

  const total = ["profit_weight","speed_weight","priority_weight","reliability_weight"]
    .reduce((s, k) => s + Number(cfg[k] || 0), 0);

  return (
    <div className="container-tight py-8" data-testid="admin-pricing-page">
      <AdminNav />
      <AdminHeader title="PRICING RULES" subtitle="Configurable business rules that drive the supplier selection engine." />

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        {FIELDS.map((f) => (
          <div key={f.key} className="border border-zinc-800 bg-[#0a0a0c] p-4 rounded-sm">
            <Label className="font-mono text-xs tracking-widest text-zinc-400 uppercase">{f.label}</Label>
            <Input data-testid={`cfg-${f.key}`} type="number" step="0.01"
              value={cfg[f.key] ?? ""} onChange={(e) => setCfg({ ...cfg, [f.key]: Number(e.target.value) })}
              className="bg-[#050505] border-zinc-800 rounded-sm mt-1 focus-visible:ring-[#FF5C00]" />
            {f.hint && <div className="font-mono text-[10px] text-zinc-600 mt-1">{f.hint}</div>}
          </div>
        ))}
      </div>

      <div className="border border-zinc-800 bg-[#0a0a0c] p-4 rounded-sm mb-6 flex items-center justify-between">
        <div>
          <div className="font-mono text-xs tracking-widest uppercase text-zinc-300">Require Human Review when no supplier</div>
          <div className="font-mono text-[10px] text-zinc-500 mt-1">If no eligible supplier passes rules, route to Human Review (never auto-cancel).</div>
        </div>
        <Switch data-testid="cfg-hitl-switch" checked={cfg.require_human_review_when_no_supplier}
          onCheckedChange={(v) => setCfg({ ...cfg, require_human_review_when_no_supplier: v })} />
      </div>

      <div className="flex items-center gap-4">
        <Button data-testid="save-pricing" disabled={saving} onClick={save}
          className="bg-[#FF5C00] hover:bg-[#FF7A33] rounded-sm font-mono tracking-wider h-11 px-6">
          {saving ? "SAVING..." : "SAVE PRICING RULES"}
        </Button>
        <div className="font-mono text-xs text-zinc-500">
          Weight total: <span className={total > 1.01 || total < 0.99 ? "text-yellow-400" : "text-green-400"}>{total.toFixed(2)}</span> (target 1.00)
        </div>
      </div>
    </div>
  );
}
